import { useEffect, useState } from 'react';
import {
  parseCurrency,
  publicPricingFallback,
  type Currency,
  type PublicPlan,
} from '@krevya/shared';
import { api } from '../../api.js';
import { isPublicPricing } from './pricing.js';

export type PublicPricing = {
  plans: readonly PublicPlan[];
  /** The currency every card is in. */
  currency: Currency;
  /**
   * False until the server has said which region this visitor is in. The
   * page keeps every amount hidden (in its place, so nothing moves) until
   * then, because the guess may be another region's currency.
   */
  settled: boolean;
};

/**
 * The prices the landing page shows, in the visitor's region's currency.
 *
 * The region decides the currency and the visitor cannot change it: the
 * server prices Turkey in lira, the euro area in euros and everyone else in
 * dollars, and offers nothing else. The page used to start from a guess (lira
 * for a Turkish page, dollars otherwise), so a visitor from Turkey reading in
 * English saw dollar prices until the answer came; the amounts now stay
 * hidden until it does.
 *
 * It replaces the fallback table only with an answer that passes
 * `isPublicPricing`. A wrong price is worse than a stale one, and a blank card
 * is worse than both, so every way the request can go wrong (offline, a 500, a
 * static host that answers with index.html, a shape from a newer API) ends the
 * same way: the guess is shown, as before.
 */
export function usePublicPricing(locale: string): PublicPricing {
  const [answer, setAnswer] = useState<{
    plans: readonly PublicPlan[];
    currency: Currency;
  } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .publicPricing()
      .then((body) => {
        if (!live) return;
        const currency = isPublicPricing(body)
          ? (parseCurrency(body.currency) ?? parseCurrency(body.plans[0]?.monthly.currency))
          : null;
        if (currency && isPublicPricing(body)) setAnswer({ plans: body.plans, currency });
        else setFailed(true);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, []);

  if (answer) return { ...answer, settled: true };
  const guess: Currency = locale === 'tr' ? 'try' : 'usd';
  return { plans: publicPricingFallback(guess), currency: guess, settled: failed };
}
